import React, { useState } from "react";
import { StyleSheet, ActivityIndicator, View, KeyboardAvoidingView, Platform, Pressable } from "react-native";
import { useRouter } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { useTheme } from "@/hooks/useTheme";
import { supabase } from "@/lib/supabase-client";
import { Image, ImageSource } from "expo-image";
import { MaterialIcons } from "@expo/vector-icons";
import Head from "expo-router/head";

export default function ForgotPassword() {
  const router = useRouter();
  const { colors } = useTheme();

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [logo] = useState<ImageSource>(require("@/assets/images/rockforce-logo-rf-yellow.svg"));

  const handleResetPassword = async () => {
    if (!email.trim()) return;

    setSubmitting(true);
    try {
      const redirectTo = `${window.location.origin}/auth/callback`;
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo });
      if (error) {
        console.error("[forgot-password] resetPasswordForEmail error:", error.message);
      }
    } catch (err) {
      console.error("[forgot-password] unexpected error:", err);
    } finally {
      setSubmitting(false);
      router.push(`/verify-otp?email=${encodeURIComponent(email.trim())}` as any);
    }
  };

  const handleBackToSignIn = () => {
    router.back();
  };

  return (
    <>
      <Head>
        <title>RockForce - Forgot Password</title>
        <meta name="description" content="" />
      </Head>
      <View style={styles.outerContainer}>
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          keyboardVerticalOffset={Platform.OS === "ios" ? 150 : 0}
          style={styles.keyboardView}
        >
          {/* Logo Section */}
          <View style={styles.logoSection}>
            <Image source={[logo]} style={styles.logo} />
            <ThemedText style={[styles.logoTitle, { color: colors.text }]}>RockForce</ThemedText>
          </View>

          {/* Reset Password Card */}
          <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
            {/* Header Icon */}
            <View style={[styles.headerIconContainer, { backgroundColor: colors.primary + "15" }]}>
              <MaterialIcons name="lock-reset" size={40} color={colors.primary} />
            </View>

            <ThemedText style={[styles.cardTitle, { color: colors.text }]}>Forgot Password?</ThemedText>
            <ThemedText style={[styles.cardSubtitle, { color: colors.textSecondary }]}>
              No worries! Enter your email and we&apos;ll send you reset instructions.
            </ThemedText>

            {/* Form */}
            <View style={styles.form}>
              <Input
                icon="email"
                placeholder="Enter your email"
                value={email}
                onChangeText={setEmail}
                autoComplete="email"
                containerStyle={styles.input}
                autoFocus={true}
                keyboardType="email-address"
                autoCapitalize="none"
                onSubmitEditing={handleResetPassword}
              />

              {submitting ? (
                <ActivityIndicator size="large" color={colors.primary} style={styles.loader} />
              ) : (
                <Button
                  text="Send Reset Code"
                  onPress={handleResetPassword}
                  icon="send"
                  iconPosition="right"
                  iconSize={18}
                  iconColor="white"
                  enabled={!!email.trim()}
                  style={[
                    styles.primaryButton,
                    {
                      backgroundColor: email.trim() ? colors.primary : colors.border,
                      opacity: email.trim() ? 1 : 0.6,
                    },
                  ]}
                  textStyle={styles.primaryButtonText}
                />
              )}
            </View>

            {/* Back to Sign In */}
            <Pressable style={[styles.backButton, { borderColor: colors.border }]} onPress={handleBackToSignIn}>
              <MaterialIcons name="arrow-back" size={18} color={colors.textSecondary} />
              <ThemedText style={[styles.backButtonText, { color: colors.textSecondary }]}>Back to Sign In</ThemedText>
            </Pressable>
          </View>
        </KeyboardAvoidingView>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    alignItems: "center",
  },
  keyboardView: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    width: "100%",
    maxWidth: 400,
  },
  logoSection: {
    alignItems: "center",
    marginBottom: 32,
  },
  logo: {
    height: 64,
    width: 64,
    marginBottom: 12,
  },
  logoTitle: {
    fontSize: 28,
    fontWeight: "800",
    letterSpacing: 2,
  },
  card: {
    width: "100%",
    borderRadius: 16,
    borderWidth: 1,
    padding: 24,
    alignItems: "center",
  },
  headerIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  cardTitle: {
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 8,
  },
  cardSubtitle: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 20,
  },
  form: {
    width: "100%",
    gap: 16,
  },
  input: {
    minWidth: "100%",
  },
  loader: {
    marginVertical: 8,
  },
  primaryButton: {
    borderRadius: 10,
    minHeight: 50,
    width: "100%",
  },
  primaryButtonText: {
    color: "white",
    fontSize: 17,
    fontWeight: "600",
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 20,
    paddingVertical: 12,
    borderTopWidth: 1,
    width: "100%",
  },
  backButtonText: {
    fontSize: 15,
    fontWeight: "500",
  },
});
